/**
 * Renders the "Recently viewed" band on product pages.
 *
 * Data source: the native `RecentlyViewed` localStorage tracker Horizon already ships
 * (`@theme/recently-viewed-products`), which every product page writes to on load. This file only
 * reads that same list and renders it — it introduces no second tracking mechanism.
 *
 * Product data (title, price, image, url) is fetched live via the Section Rendering API against the
 * theme's own "predictive-search" section, exactly the way `predictive-search.js` already does for the
 * "recently viewed" row inside the search dropdown (`q=id:<id> OR id:<id>&resources[type]=product`).
 * Reusing that path means the cards rendered here are genuine `resource-card` product cards with real,
 * current price and image — not a second card template to keep in sync.
 *
 * Renders nothing when there is no history (no fetch is even made) and nothing on fetch failure.
 */
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const ROOT_SELECTOR = '[data-recently-viewed]';
const MAX_ITEMS = 4;

/**
 * @param {string[]} ids
 * @returns {Promise<Element[]>}
 */
async function fetchProductCards(ids) {
  if (ids.length === 0 || typeof Theme === 'undefined' || !Theme.routes?.search_url) return [];

  const url = new URL(Theme.routes.search_url, window.location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');

  let html;
  try {
    html = await sectionRenderer.getSectionHTML('predictive-search', false, url);
  } catch {
    return [];
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const items = [...parsed.querySelectorAll('[ref="recentlyViewedItems[]"]')];

  return items
    .map((item) => item.firstElementChild)
    .filter((card) => card && card.textContent && card.textContent.trim().length > 0);
}

/**
 * @param {Element} root
 */
async function init(root) {
  const currentProductId = root.getAttribute('data-current-product-id');
  const grid = root.querySelector('[data-recently-viewed-grid]');
  if (!grid) return;

  const ids = RecentlyViewed.getProducts()
    .filter((id) => id !== currentProductId)
    .slice(0, MAX_ITEMS);

  if (ids.length === 0) return;

  const cards = await fetchProductCards(ids);
  if (cards.length === 0) return;

  for (const card of cards) {
    const li = document.createElement('li');
    li.className = 'recently-viewed__item';
    li.append(card);
    grid.append(li);
  }

  root.hidden = false;
}

document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
  init(root);
});
